// Bio — a player's headline card (name / position / meta rows / HOPS bar),
// ported 1:1 from js/renderers-bio.js via the 04b bridge. Ben's simplest renderer:
// display-only, no sub-controls, no picker of its own, no type-switch. Its player
// comes from the module's own .player-select (the header control) as the `playerId`
// prop; the sticky default seeds a fresh module the same way. With ?live=1 its hook
// reads the real data, for any player (T2-3).
//
// One module, player OR team (teams and matches, Ben 2026-10-30): with ?live=1 a team dropped on it
// (`_team`, live/team.ts) shows the team card instead (BioTeam.tsx), its second screen the team's
// league history. Picking a player in the search box brings the player back.
//
// The HOPS rating bar keeps the prototype's d3 width transition (450ms cubic-out).
// React must NOT own that width: we keep `width` out of the JSX style and let d3
// drive it via a ref, so a player change animates old→new width instead of React
// resetting it to 0 on every re-render (renderers-bio.js:98).

import { SubControlsSlot } from "./SubControlsSlot.js";
import { getModState, setModState } from "./bridge.js";
import { BioCareer } from "./BioCareer.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainBio, explainBioCareer, explainBioTeam, explainBioTeamHistory } from "./explain.js";
import { LIVE } from "../live/flag.js";
import { useLayoutEffect, useEffect, useRef, useState } from "react";
import { BioHops } from "./BioHops.js";
import { BioTeam, BioTeamHistory } from "./BioTeam.js";
import { teamOfModule } from "../live/team.js";
import * as d3 from "d3";
import type { ProtoModuleProps } from "./bridge.js";
import { useBioPlayer } from "../hooks/usePlayerBio.js";
import {
  humanizePositionTemplate,
  formatAge,
  calcAge,
  formatDate,
  formatHeight,
  formatWeight,
  formatLastAppearance,
  formatHopsValue,
  hopsBarWidth,
  legalNameLine,
} from "./bio-view.js";

const DASH = "—";

/** "178cm, 87kg"; one missing → just the other; neither → "—". */
function heightWeight(height: number | null | undefined, weight: number | null | undefined): string {
  const parts = [height ? `${height}cm` : null, weight ? `${weight}kg` : null].filter(Boolean);
  return parts.length ? parts.join(", ") : DASH;
}

export function BioModule({ mod, body, playerId }: ProtoModuleProps) {
  // A team shown here: the player underneath isn't loaded (nothing of theirs is on screen).
  const team = LIVE ? teamOfModule(mod) : null;
  const bio = useBioPlayer(team ? null : playerId);
  // Live: a second screen, Career (Ben, 2026-10-19 — "the whole thing should be a second screen on
  // bio"), switched by the strip's button as the Radar's Radar / Distribution. Kept on the module.
  const [view, setView] = useState<"profile" | "career">(() => (getModState<string>(mod, "_bioView") === "career" ? "career" : "profile"));
  // Switching between a player and a team opens on the first screen (the card), not the other's
  // second screen (Career / History). The widget isn't rebuilt on a refresh, so it's done here.
  const kind = team ? "team" : "player";
  const [shownKind, setShownKind] = useState(kind);
  if (shownKind !== kind) {
    setShownKind(kind);
    if (view !== "profile") {
      setModState(mod, "_bioView", undefined);
      setView("profile");
    }
  }
  const switchView = () => {
    const next = view === "career" ? "profile" : "career";
    setModState(mod, "_bioView", next === "career" ? next : undefined);
    setView(next);
  };

  // The prototype left-aligns the body once it has a player to show; the empty
  // state keeps the body's default centring (renderers-bio.js:40).
  useEffect(() => {
    body.style.justifyContent = bio || team ? "flex-start" : "";
  }, [body, bio, team]);

  const fillRef = useRef<HTMLDivElement>(null);
  const didInit = useRef(false);
  const targetWidth = bio ? hopsBarWidth(bio.hopsRating) : 0;

  // d3 owns the fill width. First paint starts at 0% (set synchronously, pre-paint,
  // so there's no full-width flash), then every render transitions to the target —
  // a player change animates from the current width, matching the prototype.
  useLayoutEffect(() => {
    const el = fillRef.current;
    if (!el) return;
    const sel = d3.select(el);
    if (!didInit.current) {
      sel.style("width", "0%");
      didInit.current = true;
    }
    sel.transition().duration(450).ease(d3.easeCubicOut).style("width", `${targetWidth}%`);
  }, [targetWidth]);

  if (team) {
    // The team's second screen is its History (league finish each season), as a player's is Career.
    return (
      <div className="bio-wrap">
        <SubControlsSlot mod={mod}>
          <button type="button" className="module-switch-btn bio-view-toggle" onClick={switchView}>
            {view === "career" ? "Profile" : "History"}
          </button>
        </SubControlsSlot>
        <ModuleExplainer mod={mod} paragraphs={view === "career" ? explainBioTeamHistory(team.name) : explainBioTeam(team.name)} />
        {view === "career" ? <BioTeamHistory team={team} /> : <BioTeam team={team} />}
      </div>
    );
  }

  if (!bio) {
    return <div className="bio-empty">No player data.</div>;
  }

  // The button names the screen it takes you to (Ben, 2026-10-19: "Career" on the profile).
  const viewButton = LIVE && (
    <SubControlsSlot mod={mod}>
      <button type="button" className="module-switch-btn bio-view-toggle" onClick={switchView}>
        {view === "career" ? "Profile" : "Career"}
      </button>
    </SubControlsSlot>
  );

  if (LIVE && view === "career" && playerId !== null) {
    return (
      <div className="bio-wrap">
        {viewButton}
        <ModuleExplainer mod={mod} paragraphs={explainBioCareer(bio.shortName)} />
        <BioCareer playerId={playerId} all />
      </div>
    );
  }

  return (
    <div className="bio-wrap">
      {viewButton}
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainBio({ name: bio.shortName, team: bio.team || null, competition: bio.competition || null })}
        />
      )}
      <div className="bio-head">
        {/* Live: no big name — it's already in the search box above (Ben, 2026-10-18). */}
        {!LIVE && <div className="bio-name">{bio.shortName}</div>}
        <div className="bio-legal">{legalNameLine(bio.shortName, bio.name)}</div>
        <div className="bio-position">{humanizePositionTemplate(bio.positionTemplate)}</div>
      </div>
      <div className="bio-meta">
        <div className="bio-meta-row">
          <span className="bio-meta-label">Team</span>
          <span className="bio-meta-value bio-team">{bio.team || DASH}</span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Competition</span>
          <span className="bio-meta-value bio-competition">{bio.competition || DASH}</span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Country</span>
          <span className="bio-meta-value bio-country">{bio.country || DASH}</span>
        </div>
        <div className="bio-meta-row">
          <span className="bio-meta-label">Age</span>
          <span className="bio-meta-value bio-age">
            {/* Live: "27 (14 Jan 1999)" (Ben, 2026-10-18). The demo keeps "27 · 14 Jan 1999". */}
            {LIVE && calcAge(bio.dob) !== null ? `${calcAge(bio.dob)} (${formatDate(bio.dob)})` : formatAge(bio.dob)}
          </span>
        </div>
        {LIVE ? (
          // Live: one row, "178cm, 87kg" (Ben, 2026-10-18); one missing → just the other.
          <div className="bio-meta-row">
            <span className="bio-meta-label">Height / Weight</span>
            <span className="bio-meta-value bio-height-weight">{heightWeight(bio.height, bio.weight)}</span>
          </div>
        ) : (
          <>
            <div className="bio-meta-row">
              <span className="bio-meta-label">Height</span>
              <span className="bio-meta-value bio-height">{formatHeight(bio.height)}</span>
            </div>
            <div className="bio-meta-row">
              <span className="bio-meta-label">Weight</span>
              <span className="bio-meta-value bio-weight">{formatWeight(bio.weight)}</span>
            </div>
          </>
        )}
        <div className="bio-meta-row">
          <span className="bio-meta-label">Last Appearance</span>
          <span className="bio-meta-value bio-last-appearance">
            {/* Live: the date, then "vs Arsenal" on a line of its own (the opponent, Ben 2026-10-16;
                two lines, 2026-10-18, so a long club name isn't cut short). The demo keeps one line
                with her team. */}
            {LIVE ? (
              <>
                <span className="bio-line">{formatLastAppearance(bio.mostRecentMatchDate, null)}</span>
                {bio.mostRecentMatchDate && bio.mostRecentOpponent && (
                  <span className="bio-line">vs {bio.mostRecentOpponent}</span>
                )}
              </>
            ) : (
              formatLastAppearance(bio.mostRecentMatchDate, bio.mostRecentTeam)
            )}
          </span>
        </div>
      </div>
      {LIVE && bio.live && bio.live.womens !== null && bio.hopsRating ? (
        <BioHops rating={bio.hopsRating.rating} womens={bio.live.womens} />
      ) : (
      <div className="bio-rating">
        <div className="bio-rating-label">
          <span>HOPS Rating</span>
          <span className="bio-rating-value">{formatHopsValue(bio.hopsRating)}</span>
        </div>
        <div className="bar-bg bio-rating-bg">
          {/* No width in style — d3 owns it (see the layout effect above). */}
          <div className="bar-fill green bio-rating-fill" ref={fillRef} />
        </div>
      </div>
      )}
    </div>
  );
}
